import React, { useRef, useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import Actions from './Actions'
import { deleteStep, updateStepName } from 'Actions/flow'
import { getStepNames } from 'Selectors/flow'
import { getAccountFeatures } from 'Selectors/account'

import Pencil from 'Assets/images/pencil.svg'
import ConfirmationModal from 'Design/components/ConfirmationModal'

import { StepContext, useFlowContext, useStepContext } from '../contexts'
import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'
import useClipboard from 'LegacyUtils/hooks/useClipboard'

const StepOptions = ({ step, onEditStepName, setShowDeleteStepModal, canCopyInternalName }) => {
  const [show, setShow] = useState(false)
  const ref = useRef()

  const { isStartingStep } = useStepContext()

  const { text: internalNameCopyText, copy } = useClipboard({ defaultText: 'Copy Internal Name' })

  const hideDropdown = () => {
    setShow(false)
  }

  const showDropdown = () => {
    setShow(true)
  }

  const copyInternalName = () => {
    copy(step.internal_name)
  }

  const onDeleteStep = () => {
    setShowDeleteStepModal(true)
  }

  useOutsideClickListener(ref, hideDropdown, show)

  return (
    <div className='options-dots' onClick={showDropdown} ref={ref}>
      {show && (
        <div className='dropdown-menu'>
          <span className='dropdown-menu-arrow' />
          {!isStartingStep && (
            <a className='dropdown-item' onClick={onEditStepName}>
              Edit step name
            </a>
          )}
          {!isStartingStep && (
            <a className='dropdown-item' onClick={onDeleteStep}>
              Delete step
            </a>
          )}
          {canCopyInternalName && (
            <a className='dropdown-item' onClick={copyInternalName}>
              {internalNameCopyText}
            </a>
          )}
        </div>
      )}
    </div>
  )
}

const StepName = ({ name, editable, isEditing, onEditStepName, stepId }) => {
  const stepNames = useSelector(getStepNames)
  const dispatch = useDispatch()

  const { handleSubmit, register, watch } = useForm({
    mode: 'onBlur',
    defaultValues: {
      stepName: name
    }
  })

  const stepName = watch('stepName')

  const handleEditClick = () => {
    if (editable) {
      onEditStepName()
    }
  }

  const update = (data) => {
    onEditStepName(false)

    const originalStepName = name
    const modifiedStepName = data.stepName

    if (modifiedStepName !== originalStepName) {
      if (stepNames.includes(modifiedStepName)) {
        toast.error('Step name already exists')
        onEditStepName(true)
      } else {
        dispatch(
          updateStepName({
            stepId,
            stepName: modifiedStepName
          })
        )
      }
    }
  }

  const onFormSubmit = (data) => {
    update(data)
  }

  if (isEditing) {
    return (
      <form
        onBlur={handleSubmit(onFormSubmit)}
        onSubmit={handleSubmit(onFormSubmit)}
        className='autosize-input flow-step-name-input'
        data-value={stepName}
      >
        <input autoFocus {...register('stepName', { minLength: 3, maxLength: 50 })} />
      </form>
    )
  }

  return (
    <div className='step-name mr-3'>
      <div className='pointer capitalize truncate' onClick={handleEditClick}>
        {stepName}
      </div>
      {editable && (
        <span className='edit-icon pointer ml-2' onClick={handleEditClick}>
          <Pencil height='17' width='17' />
        </span>
      )}
    </div>
  )
}

const Step = ({ stepId, row }) => {
  const flow = useFlowContext()
  const dispatch = useDispatch()

  const step = _.get(flow, `steps.${stepId}`, {})
  const stepRef = useRef()

  const accountFeatures = useSelector(getAccountFeatures)

  const isFeatureEnabled = useCallback(
    (featureName) => {
      const isFeatEnabled = accountFeatures?.[featureName]
      return isFeatEnabled ?? false
    },
    [accountFeatures]
  )

  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false)
  const [isEditingStepName, setIsEditingStepName] = useState(false)

  useEffect(() => {
    stepRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [])

  const handleCloseDeleteModal = () => {
    setShowDeleteStepModal(false)
  }

  const handleEditStepName = (value) => {
    setIsEditingStepName(value ?? !isEditingStepName)
  }

  const onDeleteClick = () => {
    dispatch(deleteStep({ stepId }))
    handleCloseDeleteModal()
  }

  const isStartingStep = flow.starting_step_id === stepId
  const stepName = isStartingStep ? "Bot's first step" : step.name

  return (
    <StepContext.Provider value={{ step, row, stepId, isStartingStep }}>
      <ConfirmationModal
        isOpen={showDeleteStepModal}
        title='Delete Step'
        description={`Are you sure you want to delete the step ${stepName}? It will be unlinked from exsiting steps and this action cannot be undone.`}
        onCloseButtonClick={handleCloseDeleteModal}
        onConfirmButtonClick={onDeleteClick}
        confirmButtonText='Yes'
        cancelButtonText='No'
      />
      <div ref={stepRef} className='flow-step'>
        <div className='step-header'>
          <div className='step-header-content'>
            <StepName
              name={stepName}
              editable={!isStartingStep}
              stepId={stepId}
              isEditing={isEditingStepName}
              onEditStepName={handleEditStepName}
            />
            {(!isStartingStep || isFeatureEnabled('copyInternalName')) && (
              <div className='options'>
                <StepOptions
                  step={{ ...step, stepId }}
                  onEditStepName={handleEditStepName}
                  setShowDeleteStepModal={setShowDeleteStepModal}
                  canCopyInternalName={isFeatureEnabled('copyInternalName')}
                />
              </div>
            )}
          </div>
        </div>
        <div className='step-body'>
          <Actions />
        </div>
      </div>
    </StepContext.Provider>
  )
}

export default Step
